import React, { useState, useEffect, useRef } from "react";
// import { Badge } from "@/components/ui/badge";
import { Terminal } from "lucide-react";
import { useTranslation } from "react-i18next";

const FakeLogStream: React.FC<{
  totalPages: number;
  isProcessing: boolean;
  processAsNewFile: boolean;
  isInQueue: boolean;
}> = ({ totalPages, isProcessing, processAsNewFile, isInQueue }) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const currentPageRef = useRef(0);
  const timeDelay = isInQueue ? 40000000 : processAsNewFile ? 40000 : 20000;

  useEffect(() => {
    if (!isProcessing || totalPages === 0) {
      setLogs([]);
      setCurrentPage(0);
      setStartTime(null);
      currentPageRef.current = 0;
      return;
    }

    const initialLogs = [
      t("ai_generated_events.log.timestamp_initiating", {
        timestamp: new Date().toLocaleTimeString(),
      }),
      t("ai_generated_events.log.timestamp_loading_document", {
        timestamp: new Date().toLocaleTimeString(),
        pages: totalPages,
      }),
      t("ai_generated_events.log.timestamp_extracting_text", {
        timestamp: new Date().toLocaleTimeString(),
      }),
      t("ai_generated_events.log.timestamp_analyzing_content", {
        timestamp: new Date().toLocaleTimeString(),
      }),
    ];

    setLogs(initialLogs);
    setCurrentPage(0);
    setStartTime(new Date());
    currentPageRef.current = 0;

    // Simulate page-by-page processing
    const pageInterval = setInterval(() => {
      setCurrentPage((prev) => {
        const newPage = prev + 1;
        currentPageRef.current = newPage;
        if (newPage >= totalPages) {
          clearInterval(pageInterval);
          return newPage;
        }
        return newPage;
      });
    }, timeDelay); // Process a page every 2 seconds

    // Add logs as pages are processed
    const logInterval = setInterval(() => {
      setLogs((prevLogs) => {
        const newLogs = [...prevLogs];
        const timestamp = new Date().toLocaleTimeString();
        const currentPageValue = currentPageRef.current;

        if (currentPageValue < totalPages) {
          // Add some variety to the logs without page numbers
          if (Math.random() > 0.7) {
            newLogs.push(
              t("ai_generated_events.log.timestamp_found_events", {
                timestamp,
                count: Math.floor(Math.random() * 10) + 1,
              }),
            );
          }
        }

        return newLogs.slice(-20); // Keep only last 20 logs
      });
    }, timeDelay);

    return () => {
      clearInterval(pageInterval);
      clearInterval(logInterval);
    };
  }, [isProcessing, totalPages, t, timeDelay]);

  // const progress =
  //   totalPages > 0 ? Math.min((currentPage / totalPages) * 100, 100) : 0;

  // Calculate estimated completion time
  const getEstimatedCompletionTime = () => {
    if (!startTime || totalPages === 0 || currentPage === 0) {
      return null;
    }

    const elapsedTime = Date.now() - startTime.getTime();
    const pagesCompleted = currentPage;
    const timePerPage = elapsedTime / pagesCompleted;
    const remainingPages = totalPages - currentPage;
    const estimatedTimeRemaining = remainingPages * timePerPage;

    return estimatedTimeRemaining;
  };

  // Format time in human-readable format
  const formatTimeRemaining = (milliseconds: number): string => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days} day${days > 1 ? "s" : ""}`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? "s" : ""}`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? "s" : ""}`;
    } else {
      return `${seconds} second${seconds > 1 ? "s" : ""}`;
    }
  };

  const estimatedTimeRemaining = getEstimatedCompletionTime();

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Terminal className="w-4 h-4" />
        <span className="font-medium text-sm">
          {t("ai_generated_events.label.processing_logs")}
        </span>
        {/* <Badge variant="outline" className="text-xs">
          {Math.round(progress)}%
        </Badge> */}
      </div>

      <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-xs max-h-48 overflow-y-auto">
        {logs.map((log, index) => (
          <div key={index} className="mb-1">
            {log}
          </div>
        ))}
        {isProcessing && (
          <div className="animate-pulse">
            <span className="inline-block w-2 h-4 bg-green-400 animate-pulse"></span>
          </div>
        )}
      </div>

      <div className="text-xs text-gray-600">
        {estimatedTimeRemaining
          ? t("ai_generated_events.log.estimated_completion", {
              time: formatTimeRemaining(estimatedTimeRemaining),
            })
          : t("ai_generated_events.log.calculating_estimate")}
      </div>

      {/* 
      <div className="text-xs text-gray-600 text-center">
        {t("ai_generated_events.log.progress", {
          current: Math.min(currentPage, totalPages),
          total: totalPages,
        })}
      </div> */}
    </div>
  );
};

export default FakeLogStream;
